import React, { useCallback, useState } from 'react';
import { 
  Autocomplete, 
  Box, 
  Button, 
  Card, 
  CardMedia, 
  Chip, 
  FormControl, 
  FormHelperText, 
  Paper, 
  Stack, 
  TextField 
} from '@mui/material';
import { 
  Image as ImageIcon,  
  Cancel as CancelIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import TinyMceEditor from '../../components/TinyMceEditor';

const EditMode = React.memo(({ 
  recipe, 
  onSave, 
  onCancel, 
  userId, 
  availableTags
}) => {
  
  const [recipeData, setRecipeData] = useState({
    _id: recipe?._id,
    title: recipe?.title,
    author: recipe?.author,
    picture: recipe?.picture,
    tags: recipe?.tags ?? [],
    ingredients: recipe?.ingredients,
    recipeContent: recipe?.recipeContent,
    prepTime: recipe?.prepTime,
    cookTime: recipe?.cookTime,
    totalTime: recipe?.totalTime,
    servings: recipe?.servings,
    difficulty: recipe?.difficulty,
    recipeOwnerId: userId,
  });
  const [recipeContent, setRecipeContent] = useState(recipe?.recipeContent);

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    setRecipeData({
      ...recipeData,
      image: file
    });
  };

  const handleChange = useCallback((field) => (event) => {
    const value = event.target.value;
    setRecipeData(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  const handleIngredientEdit = (event) => {
    if (event.key === 'Enter' && event.target.value.trim()) {
      event.preventDefault();
      
      const newIngredient = event.target.value.trim();
      
      if (!recipeData?.ingredients?.includes(newIngredient)) {
        setRecipeData(prev => ({
          ...prev,
          ingredients: [...(prev.ingredients || []), newIngredient]
        }));
      }
      
      event.target.value = '';
    }
  };

  const handleTagsChange = (event, newTags) => {
    setRecipeData(prev => ({
      ...prev,
      tags: newTags
    }));
  };

  return (
    <Stack spacing={3}>
      <Paper elevation={3} sx={{ p: 2 }}>
        <Stack spacing={2}>
          <TextField
            label="Recipe Title"
            variant="outlined"
            fullWidth
            required
            value={recipeData.title}
            onChange={handleChange('title')}
          />

          <TextField
            label="Recipe Author"
            variant="outlined"
            fullWidth
            required
            value={recipeData.author}
            onChange={handleChange('author')}
          />

          <Autocomplete
            multiple
            options={availableTags}
            value={recipeData.tags}
            onChange={handleTagsChange}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip label={option} {...getTagProps({ index })} key={option} />
              ))
            }
            renderInput={(params) => (
              <TextField {...params} label="Tags" placeholder="Add tags" />
            )}
          />
          <Card elevation={3}>
            <CardMedia
              component="img"
              height="300"
              image={recipeData?.picture}
              alt={recipeData?.title}
            />
          </Card>
          <Button
            variant="outlined"
            component="label"
            startIcon={<ImageIcon />}
          >
            Change Recipe Image
            <input
              type="file"
              hidden
              onChange={handleImageUpload}
              accept="image/*"
            />
          </Button>

          <FormControl fullWidth>
            <Autocomplete
              multiple
              freeSolo
              options={[]}
              value={recipeData?.ingredients || []}
              onChange={(event, newValue) => {
                setRecipeData(prev => ({
                  ...prev,
                  ingredients: newValue
                }));
              }}
              onKeyDown={handleIngredientEdit}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={option}
                    {...getTagProps({ index })}
                    key={option}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Ingredients"
                  placeholder="Add Ingredients"
                />
              )}
            />
            <FormHelperText>
              Start typing then press 'Enter' to add an ingredient
            </FormHelperText>
          </FormControl>

          {/* Your text editor component would go here */}
          <div style={{ height: '100%', border: '1px solid #ccc' }}>
            <TinyMceEditor content={recipeContent}  setContent={setRecipeContent} />
          </div>
        </Stack>
      </Paper>
      <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
        <Button
          variant="contained"
          color="error"
          fullWidth
          startIcon={<CancelIcon />}
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          fullWidth
          startIcon={<SaveIcon />}
          onClick={() => onSave({ 
            recipeData: recipeData, 
            recipeContent: recipeContent
          })}
        >
          Save Changes
        </Button>
      </Box>
    </Stack>
  );
});

export default EditMode